import React from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { LOGIN_PHONE_CODES, type LoginPhoneRegion } from './loginPolicy';

interface PhoneRegionSelectProps {
  /** 当前号码所属地区，决定图标和完整国际区号。 */
  value: LoginPhoneRegion;
  /** 请求进行中禁止改变号码身份。 */
  disabled: boolean;
  /** 交由表单清空旧号码与验证码，组件只处理选择展示。 */
  onChange: (region: LoginPhoneRegion) => void;
}

const regionLabelKeys: Record<LoginPhoneRegion, string> = {
  cn: 'loginPhoneMainland',
  hk: 'loginPhoneHongKong',
};

/** 带本地旗帜的区号选择；复用 Headless UI 的键盘、焦点与外部点击处理。 */
export function PhoneRegionSelect({
  value,
  disabled,
  onChange,
}: PhoneRegionSelectProps): React.ReactElement {
  const label = (region: LoginPhoneRegion) =>
    i18nService.t(regionLabelKeys[region]);
  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className="login-region">
        <Listbox.Button
          type="button"
          className="login-region-button"
          aria-label={`${i18nService.t('loginPhoneRegion')}：${label(value)} ${LOGIN_PHONE_CODES[value]}`}
        >
          <img
            className="login-region-flag"
            src={`/flags/${value}.svg`}
            alt=""
            aria-hidden="true"
          />
          <span>{LOGIN_PHONE_CODES[value]}</span>
          <ChevronDownIcon aria-hidden="true" />
        </Listbox.Button>
        <Listbox.Options className="login-region-options">
          {(Object.keys(LOGIN_PHONE_CODES) as LoginPhoneRegion[]).map(
            (region) => (
              <Listbox.Option
                key={region}
                value={region}
                className={({ active }) =>
                  `login-region-option${active ? ' is-active' : ''}`
                }
              >
                {({ selected }) => (
                  <>
                    <img
                      className="login-region-flag"
                      src={`/flags/${region}.svg`}
                      alt=""
                      aria-hidden="true"
                    />
                    <span>{label(region)}</span>
                    <span>{LOGIN_PHONE_CODES[region]}</span>
                    <CheckIcon
                      aria-hidden="true"
                      className={selected ? '' : 'is-hidden'}
                    />
                  </>
                )}
              </Listbox.Option>
            ),
          )}
        </Listbox.Options>
      </div>
    </Listbox>
  );
}
